import { useEffect, useRef, useState } from 'react'

import { Form, Formik, type FormikProps, type FormikTouched } from 'formik'
import { useTranslation } from 'react-i18next'
import {
    type UseQueryResult,
    useMutation,
    useQuery,
    useQueryClient
} from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { Button } from 'components/Button/Button'
import { BankAdditionalFields } from 'components/Form/BankAdditionalFields/BankAdditionalFields'
import { BankNameAutocompleteField } from 'components/Form/BankNameAutocompleteField/BankNameAutocompleteField'
import { FormikInputField } from 'components/Form/FormikInputField/FormikInputField'
import { FormikSelect } from 'components/Form/FormikSelect/FormikSelect'
import { GoogleAddressAutocompleteField } from 'components/Form/GoogleAddressAutocompleteField/GoogleAddressAutocompleteField'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { SuccessfulChange } from 'pages/settings/features/SuccessfulChange'
import { getCountries } from 'services/endpoints/country'
import {
    getBankInformation,
    getSupportedPayoutCountries,
    putBankInformation,
    putBasicPayoutSettings
} from 'services/endpoints/settings/general/payout'
import { useUserProfile } from 'store'
import { type Country } from 'types/api/country'
import { getBankFields } from 'utils/bankFieldCatalog'
import {
    findCountry,
    findRegion,
    getCountryCode,
    getCountryOptions,
    getRegionCode,
    getRegionOptions
} from 'utils/countryCatalog'
import './_BankInformation_module.scss'

const beneficiaryAccountType = [
    {
        label: 'PERSONAL',
        value: 'PERSONAL'
    },
    {
        label: 'BUSINESS',
        value: 'BUSINESS'
    }
]

interface BankFormValues {
    beneficiaryType: string
    beneficiaryAccount: string
    beneficiaryBankSwiftCode: string
    beneficiaryBankName: string
    beneficiaryBankAddress: string
    beneficiaryBankCity: string
    beneficiaryBankPostalCode: string
    bank_country: string
    bank_state: string
    routing_number: string
    extra_params: Record<string, string>
}

interface UserFormValues {
    beneficiary_first_name: string
    beneficiary_last_name: string
    beneficiary_country: string
    beneficiary_city: string
    beneficiary_state: string
    beneficiary_street: string
    beneficiary_postal_code: string
}

export const BankInformation = () => {
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [dataReady, setDataReady] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<boolean>()
    const [bankInfo, setBankInfo] = useState<BankFormValues>()
    const [userInfo, setUserInfo] = useState<UserFormValues>()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const location = useLocation()
    const pathname = location.pathname
    const profile = useUserProfile()
    const isWire = pathname.includes('/wire/')
    const [submitErrorMessage, setSubmitErrorMessage] =
        useState<string | null>(null)

    const queryClient = useQueryClient()

const payoutMethod: 'wire' | 'ach' = isWire ? 'wire' : 'ach'
const canadianPostalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/
const {
    data: supportedCountries = [],
    isLoading: supportedCountriesLoading,
    isError: supportedCountriesError
} = useQuery<Country[]>(
    ['supportedPayoutCountries', payoutMethod],
    async () => await getSupportedPayoutCountries(payoutMethod),
    {
        refetchOnWindowFocus: false,
        staleTime: Infinity
    }
)

    const countryOptions = getCountryOptions()
    const bankCountryOptions = supportedCountries.map(country => ({
        value: country.code,
        label: country.name
    }))

    const isSupportedBankCountry = (countryCode: string): boolean => {
        return supportedCountries.some(
            country => country.code === countryCode
        )
    }

    const [showRequiredErrors, setShowRequiredErrors] = useState(false)
    
    const bankFormRef = useRef<FormikProps<BankFormValues>>(null)
    const userFormRef = useRef<FormikProps<UserFormValues>>(null)

    useEffect(() => {
        const viewport = document.querySelector<HTMLMetaElement>(
            'meta[name="viewport"]'
        )

        if (!viewport) return

        const previousContent = viewport.getAttribute('content') ?? ''

        viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, interactive-widget=resizes-content'
        )

        return () => {
            viewport.setAttribute('content', previousContent)
        }
    }, [])


    // Temporarily disabled: users cannot edit existing banking details.
    /*
    const { data: bankData }: UseQueryResult<Record<string, any>> = useQuery(
        'bankInformation',
        getBankInformation,
        {
            select(data) {
                return data
            },
            refetchOnWindowFocus: false,
            keepPreviousData: true
        }
    )
    */

    const bankData: Record<string, any> = {}

    const sendPayoutInfo = useMutation(
        async (payoutInfo: any) => {
            return await putBankInformation(payoutInfo)
        },
        {
            onMutate: () => {
                setDataReady(false)
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries('user-profile')
            },
        }
    )

    const handleBankFormSubmit = (newInfo: BankFormValues) => {
        setDataReady(false)
        setBankInfo({ ...newInfo })
    }

const handleUserFormSubmit = async (values: UserFormValues) => {
    setDataReady(false)
    setSubmitErrorMessage(null)
    setUserInfo({ ...values })

    const payload = {
        ...bankInfo,
        ...values,

        payout_method: payoutMethod,

        beneficiaryBankCountryCode:
            bankInfo?.bank_country ?? '',

        beneficiaryBankStateCode:
            bankInfo?.bank_state ?? '',

        beneficiary_country_code:
            values.beneficiary_country,

        beneficiary_state_code:
            values.beneficiary_state,

        ...(bankData?.paxum_email
            ? { paxum_email: bankData.paxum_email }
            : {})
    }

    try {
        await sendPayoutInfo.mutateAsync(payload)

        setSubmitError(false)
        setCurrentPage(3)

        void putBasicPayoutSettings({
            payout_method: payoutMethod
        })
    } catch (error: any) {
        const response = error?.response?.data

        const errors =
            response?.error?.errors ??
            response?.errors ??
            {}

        const firstError = Object.values(errors)
            .flat()
            .find(
                (message): message is string =>
                    typeof message === 'string'
            )

        const message =
            firstError ??
            response?.error?.message ??
            'Unable to save bank information.'

        setSubmitErrorMessage(message)
        setSubmitError(true)
        setCurrentPage(3)
    }
}

    const renderInputCards = () => {
  
        switch (currentPage) {
            case 1: {
                const BankInfoSchema = Yup.object({
                    beneficiaryType: Yup.string().required('Required'),

                    bank_country: Yup.string()
                        .required('Required')
                        .test(
                            'supported-payout-country',
                            'This country does not support the selected payout method',
                            value => {
                                if (!value) return false

                                if (supportedCountriesLoading || supportedCountriesError) {
                                    return false
                                }

                                return isSupportedBankCountry(value)
                            }
                        ),
                    bank_state: Yup.string()
                        .max(255, 'Must be 255 characters or less')
                        .required('Required'),

                    beneficiaryBankSwiftCode: Yup.string()
                        .transform(value =>
                            typeof value === 'string'
                                ? value.trim().toUpperCase()
                                : value
                        )
                        .required('Required')
                        .matches(
                            /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
                            'Enter a valid 8 or 11 character SWIFT/BIC code'
                        )
                        .test(
                            'swift-country-match',
                            'SWIFT/BIC country must match the selected bank country',
                            function (value) {
                                if (!value) return true

                                const country = this.parent.bank_country

                                if (!country) return true

                                return value.substring(4, 6) ===
                                    String(country).toUpperCase()
                            }
                        ),

                    beneficiaryBankName: Yup.string()
                        .max(255, 'Must be 255 characters or less')
                        .required('Required'),

                    beneficiaryBankAddress: Yup.string()
                        .max(255, 'Must be 255 characters or less')
                        .required('Required'),

                    beneficiaryBankCity: Yup.string()
                        .max(255, 'Must be 255 characters or less')
                        .required('Required'),

                    beneficiaryBankPostalCode: Yup.string()
                        .trim()
                        .required('Required')
                        .when('bank_country', ([country], schema) => {
                            if (country === 'CA') {
                                return schema
                                    .min(6, 'Must be at least 6 characters')
                                    .max(7, 'Must be 7 characters or less')
                                    .matches(
                                        canadianPostalCodeRegex,
                                        'Enter a valid Canadian postal code (e.g. V5X 4J2)'
                                    )
                            }

                            return schema
                                .matches(
                                    /^[a-zA-Z0-9\s-]+$/,
                                    'Only letters and numbers allowed'
                                )
                                .min(5, 'Must be at least 5 characters')
                                .max(7, 'Must be 7 characters or less')
                        }),

                    beneficiaryAccount: Yup.string()
                        .matches(
                            /^[a-zA-Z0-9\s-]+$/,
                            'Only letters and numbers allowed'
                        )
                        .min(4, 'Must be at least 4 characters')
                        .max(24, 'Must be 24 characters or less')
                        .required('Required'),

                    routing_number: Yup.string().when(
                        ['bank_country', 'beneficiaryType'],
                        ([country, beneficiaryType], schema) => {
                            const fields = getBankFields(
                                country,
                                payoutMethod,
                                beneficiaryType
                            )

                            const requiresRouting = fields?.some(
                                field => field.name === 'ROUTING_NUMBER'
                            ) ?? false

                            return requiresRouting
                                ? schema.required('Required')
                                : schema.notRequired()
                        }
                    ),

                    extra_params: Yup.object().test(
                        'paxum-bank-fields',
                        'Invalid additional banking details',
                        function (extraParams) {
                            const parent = this.parent

                            const fields = getBankFields(
                                parent.bank_country,
                                payoutMethod,
                                parent.beneficiaryType
                            )

                            if (!fields) {
                                return this.createError({
                                    message: 'Unsupported bank transfer combination'
                                })
                            }

                            const values = (extraParams ?? {}) as Record<string, string>

                            for (const field of fields) {
                                if (field.name === 'ROUTING_NUMBER') {
                                    continue
                                }

                                const value = String(
                                    values[field.name] ?? ''
                                ).trim()

                                if (!value) {
                                    return this.createError({
                                        path: `extra_params.${field.name}`,
                                        message: `${field.label} is required`
                                    })
                                }

                                if (field.type === 'select') {
                                    const valid = field.options.some(
                                        option => option.value === value
                                    )

                                    if (!valid) {
                                        return this.createError({
                                            path: `extra_params.${field.name}`,
                                            message: `Invalid ${field.label}`
                                        })
                                    }
                                }
                            }

                            const allowedNames = new Set(
                                fields
                                    .filter(field => field.name !== 'ROUTING_NUMBER')
                                    .map(field => field.name)
                            )

                            for (const name of Object.keys(values)) {
                                if (!allowedNames.has(name)) {
                                    return this.createError({
                                        path: `extra_params.${name}`,
                                        message: 'This banking field is not supported'
                                    })
                                }
                            }

                            return true
                        }
                    )
                })

                return (
                    <div className='bankInformation__form'>
                        <Formik
                            enableReinitialize={true}
                            innerRef={bankFormRef}
                            initialValues={{
                                beneficiaryType:
                                    bankInfo?.beneficiaryType ??
                                    bankData?.beneficiaryType ??
                                    'PERSONAL',

                                bank_country:
                                    bankInfo?.bank_country ??
                                    getCountryCode(
                                        bankData?.bank_country ||
                                        bankData?.beneficiaryBankCountry ||
                                        profile?.country ||
                                        ''
                                    ),

                                bank_state:
                                    bankInfo?.bank_state ??
                                    getRegionCode(
                                        bankData?.bank_country ||
                                        bankData?.beneficiaryBankCountry ||
                                        profile?.country ||
                                        '',
                                        bankData?.bank_state ||
                                        bankData?.beneficiaryBankStateCode ||
                                        profile?.state ||
                                        ''
                                    ),

                                beneficiaryBankSwiftCode:
                                    bankInfo?.beneficiaryBankSwiftCode ??
                                    bankData?.beneficiaryBankSwiftCode ??
                                    '',

                                beneficiaryBankName:
                                    bankInfo?.beneficiaryBankName ??
                                    bankData?.beneficiaryBankName ??
                                    '',

                                beneficiaryBankAddress:
                                    bankInfo?.beneficiaryBankAddress ??
                                    bankData?.beneficiaryBankAddress ??
                                    '',

                                beneficiaryBankCity:
                                    bankInfo?.beneficiaryBankCity ??
                                    bankData?.beneficiaryBankCity ??
                                    '',

                                beneficiaryBankPostalCode:
                                    bankInfo?.beneficiaryBankPostalCode ??
                                    bankData?.beneficiaryBankPostalCode ??
                                    '',

                                beneficiaryAccount:
                                    bankInfo?.beneficiaryAccount ??
                                    bankData?.beneficiaryAccount ??
                                    '',
                                routing_number:
                                    bankInfo?.routing_number ??
                                    bankData?.routing_number ??
                                    bankInfo?.extra_params?.ROUTING_NUMBER ??
                                    bankData?.extra_params?.ROUTING_NUMBER ??
                                    '',
                                extra_params: {
                                    ...(bankData?.extra_params ?? {}),
                                    ...(bankInfo?.extra_params ?? {}),
                                    ...(!bankData?.extra_params?.TRANSIT_NUMBER && bankData?.transit_number
                                        ? { TRANSIT_NUMBER: String(bankData.transit_number) }
                                        : {}),
                                    ...(!bankData?.extra_params?.INSTITUTION_NUMBER && bankData?.institution_number
                                        ? { INSTITUTION_NUMBER: String(bankData.institution_number) }
                                        : {})
                                }
                            }}
                            validationSchema={BankInfoSchema}
                            validateOnMount={false}
                            onSubmit={values => {
                                handleBankFormSubmit(values)
                            }}
                        >
                            {({
                                setFieldValue,
                                validateForm,
                                values
                            }) => {
                                const isUS = values.bank_country === 'US'

                                return (
                                    <Form>
                                        
                                        <div className='bankinfo__grid'>
                                            <div className='bankinfo__field bankinfo__field--full'>
                                                <FormikSelect
                                                    fieldName='beneficiaryType'
                                                    label={t('Bank Account Type')}
                                                    options={beneficiaryAccountType}
                                                    selectedValue={values.beneficiaryType}
                                                    onChange={selected => {
                                                        setFieldValue(
                                                            'beneficiaryType',
                                                            selected.label
                                                        )
                                                        validateForm()
                                                    }}
                                                    onTouched={() => {}}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--full'>
                                                <GoogleAddressAutocompleteField
                                                    name='beneficiaryBankAddress'
                                                    label={t('Bank Address')}
                                                    value={values.beneficiaryBankAddress}
                                                    onValueChange={nextValue => {
                                                        setFieldValue(
                                                            'beneficiaryBankAddress',
                                                            nextValue
                                                        )
                                                    }}
                                                    onPlaceSelected={parsed => {
                                                        if (parsed.street) {
                                                            setFieldValue(
                                                                'beneficiaryBankAddress',
                                                                parsed.street
                                                            )
                                                        }
                                                        if (parsed.city) {
                                                            setFieldValue(
                                                                'beneficiaryBankCity',
                                                                parsed.city
                                                            )
                                                        }
                                                        if (parsed.countryLong) {
                                                            const normalizedCountry = parsed.countryLong.trim().toLowerCase()

                                                            const catalogCountry = findCountry(parsed.countryLong)

                                                            const matchedCountry = bankCountryOptions.find(option => {
                                                                const optionName = option.label.trim().toLowerCase()
                                                                const optionCode = option.value.trim().toUpperCase()

                                                                return (
                                                                    optionName === normalizedCountry ||
                                                                    optionCode === normalizedCountry.toUpperCase() ||
                                                                    (catalogCountry &&
                                                                        optionCode === catalogCountry.code.toUpperCase())
                                                                )
                                                            })

                                                            if (matchedCountry) {
                                                                setFieldValue('bank_country', matchedCountry.value)

                                                                if (matchedCountry.value !== 'US') {
                                                                    setFieldValue('routing_number', '')
                                                                }

                                                                const matchedCatalogCountry =
                                                                    findCountry(matchedCountry.value)

                                                                const region =
                                                                    matchedCatalogCountry && parsed.stateLong
                                                                        ? findRegion(matchedCatalogCountry, parsed.stateLong)
                                                                        : undefined

                                                                setFieldValue('bank_state', region?.code ?? '')
                                                            } else {
                                                                setFieldValue('bank_country', '')
                                                                setFieldValue('bank_state', '')
                                                                setFieldValue('routing_number', '')
                                                            }
                                                        }
                                                        if (parsed.zip) {
                                                            setFieldValue(
                                                                'beneficiaryBankPostalCode',
                                                                parsed.zip
                                                            )
                                                        }

                                                        validateForm()
                                                    }}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    label={t('Bank City')}
                                                    name='beneficiaryBankCity'
                                                    value={values.beneficiaryBankCity}
                                                    showRequiredErrors={showRequiredErrors}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                            <FormikSelect
                                                fieldName='bank_state'
                                                label={t('Bank State / Province')}
                                                options={getRegionOptions(values.bank_country)}
                                                selectedValue={values.bank_state}
                                                autoComplete='address-level1'
                                                onChange={selected => {
                                                const regionCode = getRegionCode(
                                                    values.bank_country,
                                                    selected.value
                                                )

                                                setFieldValue(
                                                    'bank_state',
                                                    regionCode || selected.value
                                                )

                                                validateForm()
                                            }}
                                                onTouched={() => {}}
                                            />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                            <FormikSelect
                                                fieldName='bank_country'
                                                label={t('Bank Country')}
                                                options={bankCountryOptions}
                                                selectedValue={values.bank_country}
                                                searchable
                                                autoComplete='country'
                                                autoPosition
                                                onChange={selected => {
                                                    const countryCode =
                                                        getCountryCode(selected.value) || selected.value

                                                    setFieldValue('bank_country', countryCode)
                                                    setFieldValue('bank_state', '')

                                                    if (countryCode !== 'US') {
                                                        setFieldValue('routing_number', '')
                                                    }
                                                }}
                                                onTouched={() => {}}
                                            />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    showRequiredErrors={showRequiredErrors}
                                                    label={t('Bank Postal Code')}
                                                    name='beneficiaryBankPostalCode'
                                                    value={values.beneficiaryBankPostalCode}
                                                />
                                            </div>

                                            {isUS && (
                                                <div className='bankinfo__field bankinfo__field--half'>
                                                    <FormikInputField
                                                        type='text'
                                                        label={t('Bank Routing Number')}
                                                        name='routing_number'
                                                        value={values.routing_number}
                                                        showRequiredErrors={showRequiredErrors}
                                                    />
                                                </div>
                                            )}

                                            <div
                                                className={`bankinfo__field ${
                                                    isUS
                                                        ? 'bankinfo__field--half'
                                                        : 'bankinfo__field--full'
                                                }`}
                                            >
                                                <FormikInputField
                                                    type='text'
                                                    label={t('Bank Swift Code')}
                                                    name='beneficiaryBankSwiftCode'
                                                    value={values.beneficiaryBankSwiftCode}
                                                    showRequiredErrors={showRequiredErrors}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <BankNameAutocompleteField
                                                    name='beneficiaryBankName'
                                                    label={t('Bank Name')}
                                                    value={values.beneficiaryBankName}
                                                    country={values.bank_country}
                                                    showRequiredErrors={showRequiredErrors}
                                                    onValueChange={nextValue => {
                                                        setFieldValue('beneficiaryBankName', nextValue)
                                                    }}
                                                    onSelect={bank => {
                                                        setFieldValue('beneficiaryBankName', bank.bank_name)

                                                        if (!values.bank_country && bank.country_code) {
                                                            const country = findCountry(bank.country_code)

                                                            if (country && isSupportedBankCountry(country.code)) {
                                                                setFieldValue('bank_country', country.code)
                                                                setFieldValue('bank_state', '')

                                                                if (country.code !== 'US') {
                                                                    setFieldValue('routing_number', '')
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    label={t('Bank Account Number')}
                                                    name='beneficiaryAccount'
                                                    value={values.beneficiaryAccount}
                                                />
                                            </div>

                                            <BankAdditionalFields
                                                method={payoutMethod}
                                                showRequiredErrors={showRequiredErrors}
                                            />
                                        </div>
                                    </Form>
                                )
                            }}
                        </Formik>
                    </div>
                )
            }

            case 2: {
                const UserInfoSchema = Yup.object({
                    beneficiary_first_name: Yup.string()
                        .max(255, t('validation:mustBe255CharactersOrLess'))
                        .required(t('validation:required')),
                    beneficiary_last_name: Yup.string()
                        .max(255, t('validation:mustBe255CharactersOrLess'))
                        .required(t('validation:required')),
                    beneficiary_country: Yup.string()
                        .max(255, t('validation:mustBe255CharactersOrLess'))
                        .required(t('validation:required')),
                    beneficiary_state: Yup.string()
                        .max(255, t('validation:mustBe255CharactersOrLess'))
                        .required(t('validation:required')),
                    beneficiary_street: Yup.string()
                        .max(255, t('validation:mustBe255CharactersOrLess'))
                        .required(t('validation:required')),
                    beneficiary_city: Yup.string()
                        .max(255, t('validation:mustBe255CharactersOrLess'))
                        .required(t('validation:required')),
                    beneficiary_postal_code: Yup.string()
                        .trim()
                        .required(t('validation:required'))
                        .when('beneficiary_country', ([country], schema) => {
                            if (country === 'CA') {
                                return schema
                                    .min(6, 'Must be at least 6 characters')
                                    .max(7, 'Must be 7 characters or less')
                                    .matches(
                                        canadianPostalCodeRegex,
                                        'Enter a valid Canadian postal code (e.g. V5X 4J2)'
                                    )
                            }

                            return schema
                                .matches(
                                    /^[a-zA-Z0-9\s-]+$/,
                                    t('validation:AlphaNumericOnly')
                                )
                                .min(5, t('validation:mustBeMin6Characters'))
                                .max(7, t('validation:mustBe7Characters'))
                        }),
                })

                return (
                    <div className='personalInformation__form'>
                        <Formik
                            enableReinitialize={true}
                            innerRef={userFormRef}
                            initialValues={{
                                beneficiary_first_name:
                                    userInfo?.beneficiary_first_name ??
                                    bankData?.beneficiary_first_name ??
                                    profile?.first_name ??
                                    '',

                                beneficiary_last_name:
                                    userInfo?.beneficiary_last_name ??
                                    bankData?.beneficiary_last_name ??
                                    profile?.last_name ??
                                    '',

                                beneficiary_country:
                                    userInfo?.beneficiary_country ??
                                    getCountryCode(
                                        bankData?.beneficiary_country ||
                                        profile?.country ||
                                        ''
                                    ),

                                beneficiary_state:
                                    userInfo?.beneficiary_state ??
                                    getRegionCode(
                                        bankData?.beneficiary_country ||
                                        profile?.country ||
                                        '',
                                        bankData?.beneficiary_state ||
                                        profile?.state ||
                                        ''
                                    ),

                                beneficiary_city:
                                    userInfo?.beneficiary_city ??
                                    bankData?.beneficiary_city ??
                                    profile?.city_name ??
                                    '',

                                beneficiary_street:
                                    userInfo?.beneficiary_street ??
                                    bankData?.beneficiary_street ??
                                    profile?.address ??
                                    '',

                                beneficiary_postal_code:
                                    userInfo?.beneficiary_postal_code ??
                                    bankData?.beneficiary_postal_code ??
                                    profile?.postal_code ??
                                    ''
                            }}
                            validationSchema={UserInfoSchema}
                            validateOnMount={false}
                            onSubmit={values => {
                                handleUserFormSubmit(values)
                            }}
                        >
                            {({ values, isValid, setFieldValue, validateForm }) => {
                                if (isValid && !dataReady) {
                                    setDataReady(true)
                                } else if (!isValid && dataReady) {
                                    setDataReady(false)
                                }

                                return (
                                    <Form>
                                        <div className='bankinfo__grid'>
                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    label={t('firstName')}
                                                    name='beneficiary_first_name'
                                                    value={values.beneficiary_first_name}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    label={t('lastName')}
                                                    name='beneficiary_last_name'
                                                    value={values.beneficiary_last_name}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--full'>
                                                <GoogleAddressAutocompleteField
                                                    name='beneficiary_street'
                                                    label={t('Address')}
                                                    value={values.beneficiary_street}
                                                    onValueChange={nextValue => {
                                                        setFieldValue(
                                                            'beneficiary_street',
                                                            nextValue
                                                        )
                                                    }}
                                                    onPlaceSelected={parsed => {
                                                        if (parsed.street) {
                                                            setFieldValue(
                                                                'beneficiary_street',
                                                                parsed.street
                                                            )
                                                        }

                                                        if (parsed.city) {
                                                            setFieldValue(
                                                                'beneficiary_city',
                                                                parsed.city
                                                            )
                                                        }

if (parsed.countryLong) {
    const country = findCountry(parsed.countryLong)

    if (country) {
        setFieldValue(
            'beneficiary_country',
            country.code
        )

        if (parsed.stateLong) {
            const region = findRegion(
                country,
                parsed.stateLong
            )

            setFieldValue(
                'beneficiary_state',
                region?.code ?? ''
            )
        }
    }
}
                                                        if (parsed.zip) {
                                                            setFieldValue(
                                                                'beneficiary_postal_code',
                                                                parsed.zip
                                                            )
                                                        }

                                                        validateForm()
                                                    }}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    label={t('beneficiaryCity')}
                                                    name='beneficiary_city'
                                                    value={values.beneficiary_city}
                                                />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                            <FormikSelect
                                                fieldName='beneficiary_state'
                                                label={t('State / Province')}
                                                options={getRegionOptions(values.beneficiary_country)}
                                                selectedValue={values.beneficiary_state}
                                                autoComplete='address-level1'
                                                autoPosition
                                                onChange={selected => {
                                                    const regionCode = getRegionCode(
                                                        values.beneficiary_country,
                                                        selected.value
                                                    )

                                                    setFieldValue(
                                                        'beneficiary_state',
                                                        regionCode || selected.value
                                                    )

                                                    validateForm()
                                                }}
                                                onTouched={() => {}}
                                            />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                            <FormikSelect
                                                fieldName='beneficiary_country'
                                                label={t('Country')}
                                                options={countryOptions}
                                                selectedValue={values.beneficiary_country}
                                                autoComplete='country'
                                                searchable
                                                openUpwards
                                                onChange={selected => {
                                                    const countryCode =
                                                        getCountryCode(selected.value) || selected.value

                                                    setFieldValue(
                                                        'beneficiary_country',
                                                        countryCode
                                                    )

                                                    setFieldValue('beneficiary_state', '')

                                                    validateForm()
                                                }}
                                                onTouched={() => {}}
                                            />
                                            </div>

                                            <div className='bankinfo__field bankinfo__field--half'>
                                                <FormikInputField
                                                    type='text'
                                                    label={t('postalCodeZipCode')}
                                                    name='beneficiary_postal_code'
                                                    value={values.beneficiary_postal_code}
                                                />
                                            </div>
                                        </div>
                                    </Form>
                                )
                            }}
                        </Formik>
                    </div>
                )
            }

            case 3:
                return (
                    <SuccessfulChange
                        img={Icons.payout_bank_big}
                        header={submitError ? t('error:error') : t('success')}
                        text={
                            submitError
                                ? submitErrorMessage ??
                                t('error:anErrorOccuredPleaseTryAgain')
                                : t('yourBankInformationHasSuccessfullyChanged')
                        }
                    />
                )

            default:
                return null
        }
    }

    const renderBottomButton = () => {
        switch (currentPage) {
            case 1:
                return (
                    <Button
                        text={<img src={Icons.arrows_next} alt={t('next')} />}
                        color='blue'
                        width='20'
                        height='5'
                        customClass='bankinfo__bottomBtn'
                        clickFn={async () => {
                            const form = bankFormRef.current
                            if (!form) return

                            const errors = await form.validateForm()

                            if (Object.keys(errors).length > 0) {
                                setShowRequiredErrors(true)

                            const touchedFields = Object.keys(errors).reduce<
                                FormikTouched<BankFormValues>
                            >((acc, key) => {
                                if (key === 'extra_params') {
                                    const extraErrors = errors.extra_params

                                    if (extraErrors && typeof extraErrors === 'object') {
                                        acc.extra_params = Object.keys(extraErrors).reduce<
                                            Record<string, boolean>
                                        >((fields, name) => {
                                            fields[name] = true
                                            return fields
                                        }, {})
                                    }
                                } else {
                                    acc[key as Exclude<keyof BankFormValues, 'extra_params'>] = true
                                }

                                return acc
                            }, {})

                            void form.setTouched(touchedFields, true)
                                return
                            }

                            setShowRequiredErrors(false)
                            setBankInfo({ ...form.values })
                            setCurrentPage(2)
                        }}
                    />
                )

            case 2:
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '1.2rem',
                            width: '100%'
                        }}
                    >
                        <Button
                            text={t('back')}
                            font='mont-16-bold'
                            color='black'
                            width='12'
                            height='5'
                            customClass='bankinfo__bottomBtn bankinfo__backBtn'
                            clickFn={() => {
                                const form = userFormRef.current

                                if (form) {
                                    setUserInfo({ ...form.values })
                                }

                                setCurrentPage(1)
                            }}
                        />

                        <Button
                            text={t('save')}
                            font='mont-16-bold'
                            color='black'
                            width='20'
                            height='5'
                            customClass='bankinfo__bottomBtn'
                            clickFn={() => {
                                userFormRef.current?.submitForm()
                            }}
                            disabled={!dataReady}
                        />
                    </div>
                )

            case 3:
                return (
                    <Button
                        text={submitError ? t('back') : t('close')}
                        font='mont-16-bold'
                        color='black'
                        width='20'
                        height='5'
                        customClass='bankinfo__bottomBtn'
                        clickFn={() => {
                            if (submitError) {
                                setSubmitError(undefined)
                                setCurrentPage(2)
                                return
                            }

                            navigate('/settings/general/payout-settings')
                        }}
                    />
                )

            default:
                return null
        }
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='dots'
                    section={isWire ? t('wire') : t('ach')}
                    title={
                        currentPage === 3
                            ? t('completed')
                            : currentPage === 2
                              ? t('account Holder')
                              : t('bankInformation')
                    }
                    dots={
                        currentPage === 3
                            ? []
                            : [
                                  {
                                      active: `${
                                          currentPage === 1
                                              ? 'active'
                                              : 'filled'
                                      }`,
                                      clickFn: () => {
                                          if (currentPage === 2) {
                                              setCurrentPage(1)
                                          }
                                      }
                                  },
                                  {
                                      active: `${
                                          currentPage === 2
                                              ? 'active'
                                              : currentPage === 3
                                                ? 'filled'
                                                : ''
                                      }`
                                  }
                              ]
                    }
                />
            }
            withoutBorder={true}
        >
            <div className='bankinfo'>
                {renderInputCards()}
                {renderBottomButton()}
            </div>
        </WithHeaderSection>
    )
}
